import { create } from 'zustand';
import type { CanvasState, Command, Frame, LayerRef } from './types';
import { applyCommand, emptyState } from './commands';
import { boundsOf } from './selectors';
import { resetAssetUrls } from '../assets/assetUrls';
import { bindYDoc, type YBinding, type BindOpts } from './ydoc/bind';
import { syncToY } from './ydoc/diff';
import { LOCAL_ORIGIN } from './ydoc/schema';
import type { SyncStatus, Awareness } from './ydoc/provider';

interface CanvasStore extends CanvasState {
  canUndo: boolean;
  canRedo: boolean;
  activeTourId: string | null;
  activeStep: number;
  annotationsVisible: boolean;
  dispatch: (c: Command) => void;
  undo: () => void;
  redo: () => void;
  beginTransaction: (label?: string) => void;
  endTransaction: () => void;
  setLayerSelection: (ref: LayerRef | null) => void;
  flashRegion: LayerRef | null;
  setFlashRegion: (ref: LayerRef | null) => void;
  layerDrag: LayerRef | null;
  setLayerDrag: (ref: LayerRef | null) => void;
  setOpenCommentThread: (id: string | null) => void;
  load: (next: CanvasState, opts?: BindOpts) => void;
  startTour: (id: string) => void;
  tourNext: () => void;
  tourPrev: () => void;
  endTour: () => void;
  toggleAnnotations: () => void;
}

const ANNOT_KEY = 'codeframe.annotationsVisible';
function initialAnnotations(): boolean {
  if (typeof localStorage === 'undefined') return true;
  try {
    return localStorage.getItem(ANNOT_KEY) !== '0';
  } catch {
    return true;
  }
}

/** Notes of a tour, ordered by step (missing step sorts last). */
export function tourNotes(frames: Frame[], tourId: string): Frame[] {
  return frames
    .filter((f) => f.kind === 'note' && f.tourId === tourId)
    .sort((a, b) => ((a.kind === 'note' ? a.step ?? 1e9 : 0) - (b.kind === 'note' ? b.step ?? 1e9 : 0)));
}

let binding: YBinding | null = null;
let txnDepth = 0;
const VIEWPORT_CMDS = new Set(['viewport.focus', 'viewport.center', 'viewport.set']);

export function storeWhenSynced(): Promise<void> { return binding?.whenSynced ?? Promise.resolve(); }
export function storeYdoc(): import('yjs').Doc | null { return binding?.ydoc ?? null; }

/** Presence/status seam — UI consumes these so it never imports `store/ydoc`
 *  directly. Awareness/onStatus belong to the *current* binding, which `load()`
 *  swaps on project change; subscribe to `onBindingChange` to re-read them. */
const bindingListeners = new Set<() => void>();
function notifyBindingChange(): void { for (const cb of bindingListeners) cb(); }
export function onBindingChange(cb: () => void): () => void {
  bindingListeners.add(cb);
  return () => { bindingListeners.delete(cb); };
}
export function storeAwareness(): Awareness | null { return binding?.awareness ?? null; }
export function storeOnSyncStatus(cb: (s: SyncStatus) => void): () => void {
  return binding?.onStatus(cb) ?? (() => {});
}

export const useCanvasStore = create<CanvasStore>((set, get) => {
  function mirror(): void {
    if (!binding) return;
    const viewport = get().doc.viewport;
    set({ doc: binding.read(viewport), canUndo: binding.undoManager.canUndo(), canRedo: binding.undoManager.canRedo() });
  }
  function rebind(doc: CanvasState['doc'], opts?: BindOpts): void {
    binding?.dispose();
    binding = bindYDoc(doc, opts);
    binding.onChange(mirror);
    txnDepth = 0;
    set({ doc: binding.read(doc.viewport), canUndo: false, canRedo: false });
    notifyBindingChange(); // awareness/status now belong to the new binding
  }

  return {
    ...emptyState(),
    canUndo: false,
    canRedo: false,
    layerSelection: null,
    flashRegion: null,
    layerDrag: null,
    openCommentThreadId: null,
    activeTourId: null,
    activeStep: 1,
    annotationsVisible: initialAnnotations(),

    dispatch: (c) => {
      if (!binding) rebind(get().doc);
      const prevS = get();
      const nextS = applyCommand(prevS, c);
      if (VIEWPORT_CMDS.has(c.t)) {
        set({ doc: { ...prevS.doc, viewport: nextS.doc.viewport } });
        return;
      }
      binding!.ydoc.transact(() => syncToY(binding!.ydoc, prevS.doc, nextS.doc), LOCAL_ORIGIN);
      const patch: Partial<CanvasState> = { selection: nextS.selection };
      if (nextS.doc.viewport !== prevS.doc.viewport) patch.doc = { ...get().doc, viewport: nextS.doc.viewport };
      set(patch);
      if (txnDepth === 0) binding!.commit();
    },

    beginTransaction: () => { txnDepth += 1; },
    endTransaction: () => { if (txnDepth === 0) return; txnDepth -= 1; if (txnDepth === 0) binding?.commit(); },
    undo: () => { binding?.undoManager.undo(); },
    redo: () => { binding?.undoManager.redo(); },

    // Layer selection is a drill-in of frame selection: picking a sub-element
    // also selects its owning frame (so panel/comment/agent seams keyed off
    // `selection` light up). Clearing the layer leaves the frame selected.
    setLayerSelection: (ref) => set(ref ? { layerSelection: ref, selection: [ref.frameId] } : { layerSelection: null }),
    setFlashRegion: (ref) => set({ flashRegion: ref }),
    setLayerDrag: (ref) => set({ layerDrag: ref }),
    setOpenCommentThread: (id) => set({ openCommentThreadId: id }),
    load: (next, opts) => {
      resetAssetUrls(); // revoke stale objectURLs before swapping docs
      set({ ...next, layerSelection: null, flashRegion: null, layerDrag: null, openCommentThreadId: null });
      rebind(next.doc, opts);
    },

    startTour: (id) => {
      set({ activeTourId: id, activeStep: 1 });
      focusStep(get, id, 1);
    },
    tourNext: () => {
      const s = get();
      if (!s.activeTourId) return;
      const n = tourNotes(s.doc.frames, s.activeTourId).length;
      const step = Math.min(s.activeStep + 1, Math.max(n, 1));
      set({ activeStep: step });
      focusStep(get, s.activeTourId, step);
    },
    tourPrev: () => {
      const s = get();
      if (!s.activeTourId) return;
      const step = Math.max(s.activeStep - 1, 1);
      set({ activeStep: step });
      focusStep(get, s.activeTourId, step);
    },
    endTour: () => set({ activeTourId: null }),
    toggleAnnotations: () => {
      const next = !get().annotationsVisible;
      if (typeof localStorage !== 'undefined') {
        try { localStorage.setItem(ANNOT_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      }
      set({ annotationsVisible: next });
    },
  };
});

export function dispatch(c: Command): void {
  useCanvasStore.getState().dispatch(c);
}

function focusStep(get: () => CanvasStore, tourId: string, step: number): void {
  const s = get();
  const ordered = tourNotes(s.doc.frames, tourId);
  const note = ordered[step - 1];
  if (!note || note.kind !== 'note') return;
  const byId = new Map(s.doc.frames.map((f) => [f.id, f] as const));
  const focusFrames: Frame[] = [note];
  const t = note.target;
  if (t && (t.kind === 'frame' || t.kind === 'element')) {
    const tf = byId.get(t.frameId);
    if (tf) focusFrames.push(tf);
  }
  const b = boundsOf(focusFrames);
  if (b) s.dispatch({ t: 'viewport.focus', bounds: { ...b, w: Math.max(b.w, 1), h: Math.max(b.h, 1) } });
}
